import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

export function MetricCard({ 
  title, 
  value, 
  change, 
  changeType = 'neutral', 
  icon: Icon,
  delay = 0,
  onClick,
  clickable = false
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  // Determine color scheme based on title
  const getColorScheme = () => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('total') || titleLower.includes('ticket')) {
      return {
        gradient: 'from-indigo-500/10 via-blue-500/10 to-indigo-500/10',
        border: 'border-l-indigo-500',
        iconBg: 'from-indigo-500 to-blue-600',
        iconHover: 'from-indigo-600 to-blue-700',
        valueHover: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400'
      };
    }
    if (titleLower.includes('resolved') || titleLower.includes('success')) {
      return {
        gradient: 'from-emerald-500/10 via-green-500/10 to-emerald-500/10',
        border: 'border-l-emerald-500',
        iconBg: 'from-emerald-500 to-green-600',
        iconHover: 'from-emerald-600 to-green-700',
        valueHover: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400'
      };
    }
    if (titleLower.includes('pending') || titleLower.includes('wait')) {
      return {
        gradient: 'from-amber-500/10 via-orange-500/10 to-amber-500/10',
        border: 'border-l-amber-500',
        iconBg: 'from-amber-500 to-orange-600',
        iconHover: 'from-amber-600 to-orange-700',
        valueHover: 'group-hover:text-amber-600 dark:group-hover:text-amber-400'
      };
    }
    if (titleLower.includes('response') || titleLower.includes('time') || titleLower.includes('avg')) {
      return {
        gradient: 'from-purple-500/10 via-cyan-500/10 to-purple-500/10',
        border: 'border-l-purple-500',
        iconBg: 'from-purple-500 to-cyan-600',
        iconHover: 'from-purple-600 to-cyan-700',
        valueHover: 'group-hover:text-purple-600 dark:group-hover:text-purple-400'
      };
    }
    // Default blue
    return {
      gradient: 'from-blue-500/10 via-indigo-500/10 to-blue-500/10',
      border: 'border-l-blue-500',
      iconBg: 'from-blue-500 to-indigo-600',
      iconHover: 'from-blue-600 to-indigo-700',
      valueHover: 'group-hover:text-blue-600 dark:group-hover:text-blue-400'
    };
  };

  const colors = getColorScheme();

  return (
    <div 
      onClick={handleClick}
      className={cn(
        "relative overflow-hidden rounded-lg bg-white dark:bg-slate-800 border-l-4 border border-gray-200 dark:border-slate-700 transition-all duration-200 group",
        colors.border,
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
        clickable && onClick && 'cursor-pointer hover:border-gray-300 dark:hover:border-slate-600 touch-manipulation'
      )}
    >
      {/* Content */}
      <div className="relative z-10 p-5 sm:p-6 flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium uppercase tracking-wide">
            {title}
          </p>
          <p className={cn(
            "text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300",
            colors.valueHover
          )}>
            {value}
          </p>
          {change && (
            <p className={cn(
              "text-xs font-medium mt-1",
              changeType === "positive" && "text-emerald-600 dark:text-emerald-400",
              changeType === "negative" && "text-red-600 dark:text-red-400",
              changeType === "neutral" && "text-gray-500 dark:text-gray-400"
            )}>
              {change}
            </p>
          )}
        </div>
        <div className={cn(
          "p-3 rounded-lg bg-gradient-to-br transition-colors duration-200",
          colors.iconBg,
          "group-hover:opacity-90"
        )}>
          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        </div>
      </div>
    </div>
  );
}

